/** Rename a session (TMMAPP-0142 item 2): `PATCH …/sessions/{id} {name}`, guarded like every editor. */
import { useRef, useState, type FormEvent } from 'react';
import { Alert, Button, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantSession } from '@/api/assistant';
import { errorCopy } from '@/api/errorCopy';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const NAME_MAX_LENGTH = 80;
const TITLE_ID = 'assistant-rename-title';

interface RenameSessionDialogProps {
  session: Pick<AssistantSession, 'id' | 'name'>;
  onClose: () => void;
  onRenamed: (session: AssistantSession) => void;
}

export function RenameSessionDialog({ session, onClose, onRenamed }: RenameSessionDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(session.name);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const trimmed = name.trim();
  const { dirty } = useDirty(name);
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmed.length === 0 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const saved = await assistantApi.patch(session.id, { name: trimmed });
      onRenamed(saved);
      onClose();
    } catch (caught) {
      setError(errorCopy(t, caught, 'assistant.error.generic'));
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="xs"
      slotProps={{ transition: { onEntered: () => nameRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{t('assistant.renameTitle')}</DialogTitle>
        <DialogContent>
          {error !== null && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label={t('assistant.dialog.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputRef={nameRef}
            required
            fullWidth
            sx={{ mt: 0.5 }}
            slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={trimmed.length === 0} loading={submitting}>
            {t('assistant.renameSave')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
